import { useEffect, useRef, useState } from 'react';
import { Loader2, PlugZap, ShieldCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ConnectionRequirement } from '@shared/types/integrations';
import { Button } from '@/components/ui/Button';
import {
  initOAuthConnection,
  openOAuthWindow,
  pollForOAuthConnection,
} from '@/lib/oauth';

export const ActionConnectionDialog = ({
  actionLabel,
  message,
  requirements,
  onClose,
  onConnected,
}: {
  actionLabel: string;
  message: string;
  requirements: ConnectionRequirement[];
  onClose: () => void;
  onConnected: (connectorKey: string) => Promise<void>;
}) => {
  const navigate = useNavigate();
  const abortRef = useRef<AbortController | null>(null);
  const [connectingKey, setConnectingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const connect = async (requirement: ConnectionRequirement) => {
    setError(null);
    if (requirement.mode !== 'oauth') {
      navigate('/connections');
      return;
    }

    setConnectingKey(requirement.connectorKey);
    try {
      const { url, attemptId } = await initOAuthConnection(requirement.connectorKey);
      const windowResult = openOAuthWindow(url);
      if (!windowResult.shouldPoll) return;

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      const result = await pollForOAuthConnection({
        provider: requirement.connectorKey,
        attemptId,
        signal: abortController.signal,
      });
      if (result.connected) {
        await onConnected(requirement.connectorKey);
      } else if (result.timedOut) {
        setError(
          'Autorisation non terminée à temps. Vous pouvez reprendre la connexion sans perdre cette action.'
        );
      }
    } catch (connectionError) {
      setError(
        connectionError instanceof Error
          ? connectionError.message
          : 'La connexion n’a pas abouti.'
      );
    } finally {
      setConnectingKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-domino-ink/35 p-4 backdrop-blur-sm">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-connection-title"
        className="w-full max-w-xl rounded-[28px] border border-domino-cardBorder bg-domino-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-domino-cardBorder px-6 py-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-domino-lilac text-domino-aubergine">
              <PlugZap className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-domino-inkSoft">
                Connexion requise
              </p>
              <h2
                id="action-connection-title"
                className="mt-1 text-xl font-semibold text-domino-ink"
              >
                {actionLabel}
              </h2>
            </div>
          </div>
          <Button size="icon" variant="ghost" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="space-y-5 px-6 py-6">
          <p className="text-sm leading-6 text-domino-inkSoft">{message}</p>

          <div className="space-y-3">
            {requirements.map((requirement) => {
              const connecting = connectingKey === requirement.connectorKey;
              return (
                <div
                  key={requirement.connectorKey}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-domino-cardBorder bg-domino-warm px-4 py-4"
                >
                  <div>
                    <p className="text-sm font-semibold text-domino-ink">
                      {requirement.label}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-domino-inkSoft">
                      L’action reprendra automatiquement après autorisation.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    disabled={connectingKey !== null}
                    onClick={() => void connect(requirement)}
                  >
                    {connecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {requirement.mode === 'oauth'
                      ? 'Connecter et continuer'
                      : 'Configurer'}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="flex items-start gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-800">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              La connexion autorise la préparation. Toute écriture restera soumise à votre
              confirmation.
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};
